import { useState, useRef, useCallback, useEffect } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Clock, CheckCircle, X, Upload, Paperclip, Image, File as FileIcon, Trash2, Building2 } from 'lucide-react';
import { ticketsAPI } from '../services/api';
import { useFeatureFlag } from '../context/featureFlagStore';
import { useCompanyStore } from '../context/companyStore';
import { useAuthStore } from '../context/authStore';
import CompanyFilter from '../components/common/CompanyFilter';

export default function TicketsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const lastRefreshTimestamp = useRef(null);

  // Initialize filter + search from URL params so both survive back-navigation.
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [filter, setFilter] = useState(searchParams.get('filter') || 'open');
  const [showNewTicket, setShowNewTicket] = useState(false);
  const canCreateTicket = useFeatureFlag('feature.ticket_create');
  const canAttachFiles = useFeatureFlag('feature.ticket_attachments');

  // Multi-company support
  const { selectedCompanyId, hasMultipleCompanies, companies } = useCompanyStore();

  // Open new ticket modal if ?new=true is in URL
  useEffect(() => {
    if (searchParams.get('new') === 'true') {
      setShowNewTicket(true);
      // Remove just the 'new' param, preserve others
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('new');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [newTicketData, setNewTicketData] = useState({ summary: '', description: '' });
  const [createError, setCreateError] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  // Co-managed IT: a client technician's queue filter (mine / unassigned).
  const { can: canDo } = useAuthStore();
  const worksQueue = canDo('tickets', 'work');
  const [assigneeFilter, setAssigneeFilter] = useState('all');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['tickets', selectedCompanyId, worksQueue ? assigneeFilter : 'all'],
    queryFn: () => ticketsAPI.list({
      pageSize: 50,
      companyId: selectedCompanyId,
      ...(worksQueue && assigneeFilter !== 'all' ? { assignee: assigneeFilter } : {}),
    }),
  });

  // Sync filter + search to URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (filter && filter !== 'open') params.set('filter', filter);
    if (search) params.set('search', search);
    setSearchParams(params, { replace: true });
  }, [filter, search, setSearchParams]);

  // Auto-refresh when returning from detail page
  useEffect(() => {
    const refreshTimestamp = location.state?.refresh;
    if (refreshTimestamp && refreshTimestamp !== lastRefreshTimestamp.current) {
      lastRefreshTimestamp.current = refreshTimestamp;
      // Restore filter + search from URL (URL is source of truth post-nav)
      setFilter(searchParams.get('filter') || 'open');
      setSearch(searchParams.get('search') || '');
      refetch();
    }
  }, [location.state?.refresh, refetch, searchParams]);

  const createTicketMutation = useMutation({
    mutationFn: async (data) => {
      // Create the ticket first
      const response = await ticketsAPI.create(data);
      const ticket = response.data.ticket;

      // Upload attachments if any
      if (attachments.length > 0) {
        setUploadProgress('Uploading attachments...');
        try {
          await ticketsAPI.uploadDocuments(ticket.id, attachments);
        } catch (uploadError) {
          console.error('Failed to upload some attachments:', uploadError);
          // Don't fail the ticket creation if attachments fail
        }
        setUploadProgress(null);
      }

      return response;
    },
    onSuccess: () => {
      setShowNewTicket(false);
      setNewTicketData({ summary: '', description: '' });
      setAttachments([]);
      setCreateError(null);
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
    onError: (error) => {
      setUploadProgress(null);
      setCreateError(error.response?.data?.error || 'Failed to create ticket');
    }
  });

  // Handle file selection from input
  const handleFileSelect = useCallback((files) => {
    const newFiles = Array.from(files).filter(file => {
      // Check for duplicates
      return !attachments.some(a => a.name === file.name && a.size === file.size);
    });
    setAttachments(prev => [...prev, ...newFiles]);
  }, [attachments]);


  // Handle drag events
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer?.files;
    if (files?.length > 0) {
      handleFileSelect(files);
    }
  }, [handleFileSelect]);

  // Remove attachment
  const removeAttachment = useCallback((index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Get file icon based on type
  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) return Image;
    return FileIcon;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Which company the ticket is for: the companies the person may raise a
  // ticket at (their role there grants it); the current filter or their
  // primary company to start with.
  const { can, user } = useAuthStore();
  const ticketCompanies = companies.filter((c) => can('tickets', 'create', c.id));
  const [newTicketCompanyId, setNewTicketCompanyId] = useState(null);
  useEffect(() => {
    if (!showNewTicket) return;
    const preferred = ticketCompanies.find((c) => c.id === selectedCompanyId) || ticketCompanies.find((c) => c.isPrimary) || ticketCompanies[0];
    setNewTicketCompanyId(preferred?.id || user?.cwCompanyId || null);
  }, [showNewTicket, selectedCompanyId]);

  const handleCreateTicket = (e) => {
    e.preventDefault();
    if (!newTicketData.summary.trim()) return;
    createTicketMutation.mutate({
      summary: newTicketData.summary.trim(),
      initialDescription: newTicketData.description.trim(),
      ...(newTicketCompanyId ? { companyId: newTicketCompanyId } : {}),
    });
  };

  const closeModal = () => {
    setShowNewTicket(false);
    setNewTicketData({ summary: '', description: '' });
    setAttachments([]);
    setCreateError(null);
    setUploadProgress(null);
  };

  const tickets = data?.data?.tickets || [];
  
  const filteredTickets = tickets.filter(t => {
    if (filter === 'open') return !t.closedFlag;
    if (filter === 'closed') return t.closedFlag;
    return true;
  }).filter(t => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return t.summary?.toLowerCase().includes(searchLower) ||
           t.company?.name?.toLowerCase().includes(searchLower);
  });

  return (
    <div className="animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Support Tickets</h1>
          <p className="text-neutral-600 dark:text-neutral-400">View and manage your support requests</p>
        </div>
        {canCreateTicket && (
          <button className="btn-primary" onClick={() => setShowNewTicket(true)}>
            <Plus className="w-5 h-5" /> New Ticket
          </button>
        )}
      </div>
      
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <input
            type="text"
            placeholder="Search tickets..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10"
          />
        </div>
        <CompanyFilter />
        {worksQueue && (
          <select
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
            className="input w-full sm:w-44"
            title="Who is working the ticket"
          >
            <option value="all">Whole queue</option>
            <option value="mine">Mine</option>
            <option value="unassigned">Unassigned</option>
          </select>
        )}
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="input w-full sm:w-40"
        >
          <option value="all">All Tickets</option>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
        </select>
      </div>
      
      {/* Tickets list */}
      <div className="card">
        {isLoading ? (
          <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">Loading tickets...</div>
        ) : filteredTickets.length === 0 ? (
          <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">No tickets found</div>
        ) : (
          <div className="divide-y divide-neutral-100 dark:divide-neutral-700">
            {filteredTickets.map((ticket) => (
              <Link
                key={ticket.id}
                to={`/tickets/${ticket.id}`}
                onClick={() => sessionStorage.setItem('ticketsReturnUrl', window.location.pathname + window.location.search)}
                className="flex items-center gap-4 p-4 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors"
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${ticket.closedFlag ? 'bg-green-100 dark:bg-green-900/50' : 'bg-amber-100 dark:bg-amber-900/50'}`}>
                  {ticket.closedFlag ? <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" /> : <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-neutral-900 dark:text-white truncate">{ticket.summary}</p>
                  <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
                    <span>#{ticket.id} • {ticket.status?.name || 'Open'} • {ticket.priority?.name || 'Normal'}</span>
                    {worksQueue && 'portalAssignee' in ticket && (ticket.withProvider ? (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300">
                        With your support provider
                      </span>
                    ) : (
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${ticket.portalAssignee || ticket.owner?.name ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'}`}>
                        {ticket.portalAssignee?.name || ticket.owner?.name || 'Unassigned'}
                      </span>
                    ))}
                    {hasMultipleCompanies && ticket.company?.name && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-neutral-100 dark:bg-neutral-700 rounded text-xs">
                        <Building2 className="w-3 h-3" />
                        {ticket.company.name}
                      </span>
                    )}
                  </div>
                </div>
                <span className={`badge ${ticket.closedFlag ? 'badge-success' : 'badge-warning'}`}>
                  {ticket.closedFlag ? 'Closed' : 'Open'}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* New Ticket Modal */}
      {showNewTicket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Create New Ticket</h2>
              <button
                onClick={closeModal}
                className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded"
              >
                <X className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
              </button>
            </div>
            <form onSubmit={handleCreateTicket} className="p-4">
              {createError && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                  {createError}
                </div>
              )}
              {uploadProgress && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg text-blue-700 dark:text-blue-300 text-sm">
                  {uploadProgress}
                </div>
              )}
              {ticketCompanies.length > 1 && (
                <div className="mb-4">
                  <label className="label">Company</label>
                  <select
                    value={newTicketCompanyId || ''}
                    onChange={(e) => setNewTicketCompanyId(Number(e.target.value))}
                    className="input w-full"
                  >
                    {ticketCompanies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="mb-4">
                <label className="label">Subject *</label>
                <input
                  type="text"
                  value={newTicketData.summary}
                  onChange={(e) => setNewTicketData({ ...newTicketData, summary: e.target.value })}
                  placeholder="Brief description of the issue"
                  className="input w-full"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="label">Description</label>
                <textarea
                  value={newTicketData.description}
                  onChange={(e) => setNewTicketData({ ...newTicketData, description: e.target.value })}
                  placeholder="Provide more details about your request..."
                  rows={6}
                  className="input w-full"
                />
              </div>

              {/* File Attachments */}
              {canAttachFiles && (
                <div className="mb-4">
                  <label className="label">Attachments</label>

                  {/* Drop Zone */}
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                      isDragging
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                        : 'border-neutral-300 dark:border-neutral-600 hover:border-neutral-400 dark:hover:border-neutral-500'
                    }`}
                  >
                    <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragging ? 'text-primary-500' : 'text-neutral-400'}`} />
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">
                      Drag & drop files here
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={(e) => handleFileSelect(e.target.files)}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                    >
                      <Paperclip className="w-4 h-4" />
                      Browse files
                    </button>
                  </div>

                  {/* Attachment List */}
                  {attachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {attachments.map((file, index) => {
                        const FileIcon = getFileIcon(file);
                        return (
                          <div
                            key={`${file.name}-${index}`}
                            className="flex items-center gap-3 p-2 bg-neutral-50 dark:bg-neutral-700 rounded-lg"
                          >
                            {file.type.startsWith('image/') ? (
                              <img
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                className="w-10 h-10 object-cover rounded"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-neutral-200 dark:bg-neutral-600 rounded flex items-center justify-center">
                                <FileIcon className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">{file.name}</p>
                              <p className="text-xs text-neutral-500 dark:text-neutral-400">{formatFileSize(file.size)}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeAttachment(index)}
                              className="p-1 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded text-neutral-500 dark:text-neutral-400 hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newTicketData.summary.trim() || createTicketMutation.isPending}
                  className="btn-primary"
                >
                  {createTicketMutation.isPending
                    ? (uploadProgress ? 'Uploading...' : 'Creating...')
                    : 'Create Ticket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
